import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Stack, Button, Skeleton } from '@mui/material';
import { useContent } from '../../../../context/ContentContext'; // Import your context
import BankDetails from './BankDetails'; // Import the BankDetails component
import PasswordDialog from './PasswordDialog';
import WithdrawSaldo from './WithdrawSaldo'; // Import WithdrawSaldo component

const DebitCard = () => {
    const { updateBankData, BankData, isNewUser, loading, error, verifyPassword } = useContent();
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [bankDetailsDialogOpen, setBankDetailsDialogOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false); // State for WithdrawSaldo dialog
    const [formData, setFormData] = useState({
        bank_name: '',
        bank_account: '',
        name_of_bank_account: '',
        saldo: 0, // Added saldo field
    });
    const [actionType, setActionType] = useState(''); // Track action type (update bank or withdraw saldo)

    // Open dialog automatically if isNewUser is true on mount
    useEffect(() => {
        console.log('isNewUser:', isNewUser); // Log isNewUser when component mounts

        if (isNewUser) {
            setPasswordDialogOpen(true);
        }
    }, [isNewUser]); // Dependency array ensures this runs whenever isNewUser changes

    const getCurrentMonthYear = () => {
        const date = new Date();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        return `${month}/${year}`;
    };

    const formatCardNumber = (cardNumber) => {
        return cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ');
    };

    const handleOpenPasswordDialog = (action) => {
        setActionType(action); // Set the action type when opening the password dialog
        setPasswordDialogOpen(true); // Open the password dialog
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        console.log(`${name}: ${value}`); // Log the field name and the value being typed
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePasswordVerification = async (password) => {
        console.log('Attempting to verify password:', password);

        const isValid = await verifyPassword(password);

        if (isValid) {
            console.log('Password is valid');
            setPasswordDialogOpen(false); // Close the password dialog

            if (actionType === 'updateBank') {
                setBankDetailsDialogOpen(true); // Open the bank details dialog
            } else if (actionType === 'withdrawSaldo') {
                setDialogOpen(true); // Open the withdraw saldo dialog
            }
        } else {
            console.log('Invalid password');
        }
    };

    const handleCloseDialog = () => {
        setBankDetailsDialogOpen(false); // Close the Bank Details dialog
        setDialogOpen(false); // Close the WithdrawSaldo dialog
    };

    const handleBankDetailsSubmit = async () => {
        if (!formData.bank_name || !formData.bank_account || !formData.name_of_bank_account) {
            alert('All fields must be filled');
            return;
        }
        await updateBankData(formData); // Submit the form data
        setBankDetailsDialogOpen(false); // Close the dialog
    };

    const handleWithdrawSubmit = async () => {
        if (formData.saldo <= 10000) {
            alert('Minimum jumlah penarikan Rp. 10.000');
            return;
        }

        // Logic for submitting the withdrawal request
        console.log('Withdrawing saldo:', formData.saldo);
        // Add any API call here to process the withdrawal

        // After submission, close the WithdrawSaldo dialog
        setDialogOpen(false);
    };

    return (
        <>
            <Card
                sx={{
                    width: 320,
                    height: 190,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                    color: 'white',
                    boxShadow: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: 2,
                    position: 'relative',
                }}
            >
                <img
                    src="/images/mastercard.svg"
                    alt="MasterCard"
                    style={{
                        position: 'absolute',
                        top: 20,
                        right: 10,
                        width: 40,
                        height: 'auto',
                    }}
                />

                <CardContent sx={{ paddingBottom: 0 }}>
                    {loading ? (
                        <Skeleton variant="text" width={120} height={30} />
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            {BankData?.bank_name || 'Bank Name'}
                        </Typography>
                    )}

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {loading ? (
                            <Skeleton variant="text" width={150} height={20} />
                        ) : (
                            <Typography variant="body1" sx={{ fontSize: 14 }}>
                                {formatCardNumber(BankData?.bank_account || '0000000000')}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {loading ? (
                        <Skeleton variant="text" width={120} height={20} />
                    ) : (
                        <Typography variant="caption" sx={{ fontSize: 12 }}>
                            {BankData?.name_of_bank_account || 'Account Holder'}
                        </Typography>
                    )}

                    <Typography variant="caption" sx={{ fontSize: 12 }}>
                        Exp: {getCurrentMonthYear()}
                    </Typography>
                </Stack>
            </Card>

            <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleOpenPasswordDialog('updateBank')} // Trigger password dialog for updating bank
                    sx={{ marginTop: 2 }}
                >
                    Update Bank
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleOpenPasswordDialog('withdrawSaldo')} // Trigger password dialog for withdrawing saldo
                    sx={{ marginTop: 2 }}
                >
                    Withdraw Saldo
                </Button>
            </Stack>

            <PasswordDialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
                onSubmit={handlePasswordVerification}
                error={error}
            />
            <BankDetails
                dialogOpen={bankDetailsDialogOpen}
                isNewUser={isNewUser}
                loading={loading}
                error={error}
                formData={formData}
                setFormData={setFormData}
                handleInputChange={handleInputChange}
                handleSubmit={handleBankDetailsSubmit}
                handleCloseDialog={handleCloseDialog}
            />
            <WithdrawSaldo
                dialogOpen={dialogOpen}
                error={error}
                loading={loading}
                formData={formData}
                setFormData={setFormData}
                handleInputChange={handleInputChange}
                handleSubmit={handleWithdrawSubmit}
                handleCloseDialog={handleCloseDialog}
            />
        </>
    );
};

export default DebitCard;
